import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, CardBody, CardTitle, CardText,
    Button, Table, Badge, Spinner, Alert, Progress,
    Nav, NavItem, NavLink, TabContent, TabPane, Navbar, NavbarBrand
} from 'reactstrap';
import {
    Users, TrendingUp, DollarSign, Package, Activity,
    ArrowUp, ArrowDown, Eye, Edit, Trash2, Plus,
    Download, Filter, Search, Shield, Settings, X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { slide as Menu } from 'react-burger-menu';

// Import SCSS
import './AdminDashboard.scss';

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('1');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalTransactions: 0,
        totalVolume: 0,
        activeUsers: 0,
        pendingTransactions: 0
    });
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [recentUsers, setRecentUsers] = useState([]);
    const [assets, setAssets] = useState([]);
    const [userActivities, setUserActivities] = useState([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            // Fetch total users
            const { count: userCount, error: userError } = await supabase
                .from('profiles')
                .select('*', { count: 'exact', head: true });

            if (userError) throw userError;

            // Fetch total transactions and volume
            const { data: transactions, error: transError } = await supabase
                .from('transactions')
                .select('amount, status');

            if (transError) throw transError;

            // Fetch pending transactions
            const pendingTrans = transactions.filter(t => t.status === 'pending').length;

            // Calculate total volume
            const totalVolume = transactions
                .filter(t => t.status === 'completed')
                .reduce((sum, t) => sum + parseFloat(t.amount), 0);

            // Fetch recent transactions with user and asset details
            const { data: recentTrans, error: recentTransError } = await supabase
                .from('transactions')
                .select(`
                    *,
                    profiles:user_id(first_name, last_name),
                    assets(currency_code, currency_name)
                `)
                .order('created_at', { ascending: false })
                .limit(10);

            if (recentTransError) throw recentTransError;

            // Fetch recent users
            const { data: users, error: usersError } = await supabase
                .from('profiles')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(8);

            if (usersError) throw usersError;

            // Fetch assets
            const { data: assetsData, error: assetsError } = await supabase
                .from('assets')
                .select('*')
                .order('sort_order', { ascending: true });

            if (assetsError) throw assetsError;

            // Fetch user activities
            const { data: activities, error: activitiesError } = await supabase
                .from('user_activities')
                .select(`
                    *,
                    profiles:user_id(first_name, last_name, email)
                `)
                .order('created_at', { ascending: false })
                .limit(20);

            if (activitiesError) throw activitiesError;

            setStats({
                totalUsers: userCount || 0,
                totalTransactions: transactions.length,
                totalVolume,
                activeUsers: Math.floor(userCount * 0.65), // Simulated active users
                pendingTransactions: pendingTrans
            });

            setRecentTransactions(recentTrans || []);
            setRecentUsers(users || []);
            setAssets(assetsData || []);
            setUserActivities(activities || []);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pending': { color: 'warning', text: 'Pending' },
            'completed': { color: 'success', text: 'Completed' },
            'failed': { color: 'danger', text: 'Failed' },
            'cancelled': { color: 'secondary', text: 'Cancelled' }
        };
        const config = statusConfig[status] || { color: 'secondary', text: status };
        return <Badge color={config.color} className="admin-badge">{config.text}</Badge>;
    };

    const getTransactionTypeIcon = (type) => {
        const icons = {
            'deposit': <ArrowUp size={14} className="transaction-icon transaction-icon--deposit" />,
            'withdrawal': <ArrowDown size={14} className="transaction-icon transaction-icon--withdrawal" />,
            'transfer': <Activity size={14} className="transaction-icon transaction-icon--transfer" />
        };
        return icons[type] || <Activity size={14} className="transaction-icon" />;
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatCrypto = (amount, decimals = 8) => {
        return parseFloat(amount).toFixed(decimals);
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    if (loading) {
        return (
            <Container className="admin-loading-container">
                <Spinner color="primary" className="admin-loading-spinner" />
            </Container>
        );
    }

    return (
        <div className="admin-dashboard-wrapper">
            {/* Mobile Sidebar Menu */}
            <Menu
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                customBurgerIcon={false}
                width={280}
                className="admin-mobile-sidebar"
            >
                <div className="admin-sidebar-header">
                    <h3 className="admin-sidebar-title">Admin Menu</h3>
                    <button className="admin-sidebar-close" onClick={() => setSidebarOpen(false)}>
                        <X size={24} />
                    </button>
                </div>
                <nav className="admin-sidebar-nav">
                    <a href="#" className="admin-sidebar-link active">
                        <Activity size={18} />
                        <span>Dashboard</span>
                    </a>
                    <a href="#" className="admin-sidebar-link">
                        <Users size={18} />
                        <span>Users</span>
                    </a>
                    <a href="#" className="admin-sidebar-link">
                        <DollarSign size={18} />
                        <span>Transactions</span>
                    </a>
                    <a href="#" className="admin-sidebar-link">
                        <Package size={18} />
                        <span>Assets</span>
                    </a>
                    <a href="#" className="admin-sidebar-link">
                        <Shield size={18} />
                        <span>Security</span>
                    </a>
                    <a href="#" className="admin-sidebar-link">
                        <Settings size={18} />
                        <span>Settings</span>
                    </a>
                </nav>
            </Menu>

            {/* Mobile Top Bar */}
            <Navbar className="admin-mobile-topbar" expand="md">
                <Button
                    color="link"
                    className="admin-sidebar-toggle"
                    onClick={toggleSidebar}
                >
                    <Menu size={24} />
                </Button>
                <NavbarBrand className="admin-mobile-brand">Admin Dashboard</NavbarBrand>
            </Navbar>

            <Container fluid className="admin-dashboard-container">
                {/* Header */}
                <Row className="admin-header-row">

                    <div className="d-flex admin-header-content">
                        <div>
                            <h1 className="admin-title">Admin Dashboard</h1>
                            <p className="admin-subtitle">
                                Welcome back! Here's what's happening with your platform.
                            </p>
                        </div>
                        <div className="admin-header-actions">
                            <Button color="outline-primary" className="admin-btn admin-btn--outline">
                                <Download size={16} className="admin-btn-icon" />
                                Export Report
                            </Button>
                            <Button color="primary" className="admin-btn admin-btn--primary">
                                <Settings size={16} className="admin-btn-icon" />
                                Settings
                            </Button>
                        </div>
                    </div>

                </Row>

                {/* Stats Cards */}
                <Row className="admin-stats-row">
                    <Col xl={3} lg={6} className="admin-stats-col">
                        <Card className="admin-stats-card admin-stats-card--users">
                            <CardBody>
                                <div className="admin-stats-content">
                                    <div>
                                        <CardText className="admin-stats-label">Total Users</CardText>
                                        <CardTitle tag="h3" className="admin-stats-value">{stats.totalUsers}</CardTitle>
                                        <small className="admin-stats-trend admin-stats-trend--positive">
                                            <TrendingUp size={14} className="admin-stats-trend-icon" />
                                            +12% from last month
                                        </small>
                                    </div>
                                    <div className="admin-stats-icon admin-stats-icon--primary">
                                        <Users size={24} />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} lg={6} className="admin-stats-col">
                        <Card className="admin-stats-card admin-stats-card--volume">
                            <CardBody>
                                <div className="admin-stats-content">
                                    <div>
                                        <CardText className="admin-stats-label">Total Volume</CardText>
                                        <CardTitle tag="h3" className="admin-stats-value">
                                            {formatCurrency(stats.totalVolume)}
                                        </CardTitle>
                                        <small className="admin-stats-trend admin-stats-trend--positive">
                                            <TrendingUp size={14} className="admin-stats-trend-icon" />
                                            +8% from last month
                                        </small>
                                    </div>
                                    <div className="admin-stats-icon admin-stats-icon--success">
                                        <DollarSign size={24} />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} lg={6} className="admin-stats-col">
                        <Card className="admin-stats-card admin-stats-card--active">
                            <CardBody>
                                <div className="admin-stats-content">
                                    <div>
                                        <CardText className="admin-stats-label">Active Users</CardText>
                                        <CardTitle tag="h3" className="admin-stats-value">{stats.activeUsers}</CardTitle>
                                        <small className="admin-stats-info">
                                            65% of total users
                                        </small>
                                    </div>
                                    <div className="admin-stats-icon admin-stats-icon--info">
                                        <Activity size={24} />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} lg={6} className="admin-stats-col">
                        <Card className="admin-stats-card admin-stats-card--pending">
                            <CardBody>
                                <div className="admin-stats-content">
                                    <div>
                                        <CardText className="admin-stats-label">Pending Transactions</CardText>
                                        <CardTitle tag="h3" className="admin-stats-value">{stats.pendingTransactions}</CardTitle>
                                        <small className="admin-stats-trend admin-stats-trend--warning">
                                            Needs attention
                                        </small>
                                    </div>
                                    <div className="admin-stats-icon admin-stats-icon--warning">
                                        <Package size={24} />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Main Content Tabs */}
                <Card className="admin-main-card">
                    <CardBody className="admin-main-card-body">
                        <Nav tabs className="admin-main-tabs">
                            <NavItem>
                                <NavLink
                                    className={classnames('admin-tab-link', { active: activeTab === '1' })}
                                    onClick={() => setActiveTab('1')}
                                >
                                    <Activity size={16} className="admin-tab-icon" />
                                    Overview
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames('admin-tab-link', { active: activeTab === '2' })}
                                    onClick={() => setActiveTab('2')}
                                >
                                    <Users size={16} className="admin-tab-icon" />
                                    Users ({recentUsers.length})
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames('admin-tab-link', { active: activeTab === '3' })}
                                    onClick={() => setActiveTab('3')}
                                >
                                    <DollarSign size={16} className="admin-tab-icon" />
                                    Transactions ({recentTransactions.length})
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames('admin-tab-link', { active: activeTab === '4' })}
                                    onClick={() => setActiveTab('4')}
                                >
                                    <Package size={16} className="admin-tab-icon" />
                                    Assets ({assets.length})
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames('admin-tab-link', { active: activeTab === '5' })}
                                    onClick={() => setActiveTab('5')}
                                >
                                    <Shield size={16} className="admin-tab-icon" />
                                    Activity Log
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab} className="admin-tab-content">
                            {/* Overview Tab */}
                            <TabPane tabId="1" className="admin-tab-pane">
                                <Row>
                                    <Col lg={8}>
                                        <Card className="admin-section-card">
                                            <CardBody>
                                                <CardTitle tag="h5" className="admin-section-title">
                                                    Recent Transactions
                                                </CardTitle>
                                                <div className="admin-table-responsive">
                                                    <Table hover className="admin-table">
                                                        <thead className="admin-table-head">
                                                            <tr>
                                                                <th>User</th>
                                                                <th>Type</th>
                                                                <th>Asset</th>
                                                                <th>Amount</th>
                                                                <th>Status</th>
                                                                <th>Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="admin-table-body">
                                                            {recentTransactions.slice(0, 5).map(transaction => (
                                                                <tr key={transaction.id} className="admin-table-row">
                                                                    <td>
                                                                        {transaction.profiles ?
                                                                            `${transaction.profiles.first_name} ${transaction.profiles.last_name}` :
                                                                            'Unknown User'
                                                                        }
                                                                    </td>
                                                                    <td>
                                                                        <div className="admin-transaction-type">
                                                                            {getTransactionTypeIcon(transaction.transaction_type)}
                                                                            <span className="admin-transaction-type-label">
                                                                                {transaction.transaction_type}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <Badge color="outline-secondary" className="admin-asset-badge">
                                                                            {transaction.assets?.currency_code}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="admin-amount-cell">
                                                                        {formatCrypto(transaction.amount)} {transaction.assets?.currency_code}
                                                                    </td>
                                                                    <td>
                                                                        {getStatusBadge(transaction.status)}
                                                                    </td>
                                                                    <td className="admin-date-cell">
                                                                        {new Date(transaction.created_at).toLocaleDateString()}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col lg={4}>
                                        <Card className="admin-section-card">
                                            <CardBody>
                                                <CardTitle tag="h5" className="admin-section-title">
                                                    Platform Statistics
                                                </CardTitle>
                                                <div className="admin-progress-section">
                                                    <div className="admin-progress-item">
                                                        <small className="admin-progress-label">User Registration Growth</small>
                                                        <Progress value={75} color="success" className="admin-progress-bar" />
                                                        <div className="admin-progress-info">
                                                            <small>75% Target</small>
                                                            <small className="admin-progress-change">+12%</small>
                                                        </div>
                                                    </div>
                                                    <div className="admin-progress-item">
                                                        <small className="admin-progress-label">Transaction Success Rate</small>
                                                        <Progress value={92} color="info" className="admin-progress-bar" />
                                                        <div className="admin-progress-info">
                                                            <small>92% Success</small>
                                                            <small className="admin-progress-change">8% Failed</small>
                                                        </div>
                                                    </div>
                                                    <div className="admin-progress-item">
                                                        <small className="admin-progress-label">Asset Distribution</small>
                                                        <Progress multi className="admin-progress-bar admin-progress-bar--multi">
                                                            <Progress bar value={40} color="primary">Crypto 40%</Progress>
                                                            <Progress bar value={60} color="success">Fiat 60%</Progress>
                                                        </Progress>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>

                                        <Card className="admin-quick-actions-card">
                                            <CardBody>
                                                <CardTitle tag="h5" className="admin-section-title">
                                                    Quick Actions
                                                </CardTitle>
                                                <div className="admin-quick-actions">
                                                    <Button color="primary" className="admin-btn admin-btn--block">
                                                        <Plus size={16} className="admin-btn-icon" />
                                                        Add New Asset
                                                    </Button>
                                                    <Button color="outline-primary" className="admin-btn admin-btn--block admin-btn--outline">
                                                        <Users size={16} className="admin-btn-icon" />
                                                        Manage Users
                                                    </Button>
                                                    <Button color="outline-success" className="admin-btn admin-btn--block admin-btn--outline">
                                                        <DollarSign size={16} className="admin-btn-icon" />
                                                        View Reports
                                                    </Button>
                                                    <Button color="outline-warning" className="admin-btn admin-btn--block admin-btn--outline">
                                                        <Settings size={16} className="admin-btn-icon" />
                                                        System Settings
                                                    </Button>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </TabPane>

                            {/* Users Tab */}
                            <TabPane tabId="2" className="admin-tab-pane">
                                <div className="admin-section-header">
                                    <CardTitle tag="h5" className="admin-section-title">User Management</CardTitle>
                                    <div className="admin-section-actions">
                                        <Button color="outline-secondary" className="admin-btn admin-btn--outline">
                                            <Filter size={16} className="admin-btn-icon" />
                                            Filter
                                        </Button>
                                        <Button color="outline-secondary" className="admin-btn admin-btn--outline">
                                            <Download size={16} className="admin-btn-icon" />
                                            Export
                                        </Button>
                                    </div>
                                </div>
                                <div className="admin-table-responsive">
                                    <Table hover className="admin-table admin-table--users">
                                        <thead className="admin-table-head">
                                            <tr>
                                                <th>User</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>Status</th>
                                                <th>Joined</th>
                                                <th>Assets</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="admin-table-body">
                                            {recentUsers.map(user => (
                                                <tr key={user.id} className="admin-table-row">
                                                    <td>
                                                        <div className="admin-user-cell">
                                                            <div className="admin-user-avatar">
                                                                {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                                                            </div>
                                                            <div className="admin-user-info">
                                                                <div className="admin-user-name">
                                                                    {user.first_name} {user.last_name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="admin-email-cell">{user.email}</td>
                                                    <td>
                                                        <Badge color={user.role === 'admin' ? 'danger' : 'primary'} className="admin-role-badge">
                                                            {user.role}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge color="success" className="admin-status-badge">Active</Badge>
                                                    </td>
                                                    <td className="admin-date-cell">
                                                        {new Date(user.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td>
                                                        <Badge color="outline-secondary" className="admin-assets-badge">5 Assets</Badge>
                                                    </td>
                                                    <td>
                                                        <div className="admin-action-buttons">
                                                            <Button color="outline-primary" className="admin-action-btn">
                                                                <Eye size={14} />
                                                            </Button>
                                                            <Button color="outline-warning" className="admin-action-btn">
                                                                <Edit size={14} />
                                                            </Button>
                                                            <Button color="outline-danger" className="admin-action-btn">
                                                                <Trash2 size={14} />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </TabPane>

                            {/* Transactions Tab */}
                            <TabPane tabId="3" className="admin-tab-pane">
                                <div className="admin-section-header">
                                    <CardTitle tag="h5" className="admin-section-title">Transaction History</CardTitle>
                                    <div className="admin-section-actions">
                                        <Button color="outline-secondary" className="admin-btn admin-btn--outline">
                                            <Filter size={16} className="admin-btn-icon" />
                                            Filter
                                        </Button>
                                        <Button color="outline-secondary" className="admin-btn admin-btn--outline">
                                            <Download size={16} className="admin-btn-icon" />
                                            Export
                                        </Button>
                                    </div>
                                </div>
                                <div className="admin-table-responsive">
                                    <Table hover className="admin-table admin-table--transactions">
                                        <thead className="admin-table-head">
                                            <tr>
                                                <th>ID</th>
                                                <th>User</th>
                                                <th>Type</th>
                                                <th>Asset</th>
                                                <th>Amount</th>
                                                <th>Fee</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="admin-table-body">
                                            {recentTransactions.map(transaction => (
                                                <tr key={transaction.id} className="admin-table-row">
                                                    <td className="admin-id-cell">
                                                        <small className="admin-transaction-id">
                                                            {transaction.id.slice(0, 8)}...
                                                        </small>
                                                    </td>
                                                    <td>
                                                        {transaction.profiles ?
                                                            `${transaction.profiles.first_name} ${transaction.profiles.last_name}` :
                                                            'Unknown User'
                                                        }
                                                    </td>
                                                    <td>
                                                        <div className="admin-transaction-type">
                                                            {getTransactionTypeIcon(transaction.transaction_type)}
                                                            <span className="admin-transaction-type-label">
                                                                {transaction.transaction_type}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge color="outline-secondary" className="admin-asset-badge">
                                                            {transaction.assets?.currency_code}
                                                        </Badge>
                                                    </td>
                                                    <td className="admin-amount-cell">
                                                        {formatCrypto(transaction.amount)} {transaction.assets?.currency_code}
                                                    </td>
                                                    <td className="admin-fee-cell">
                                                        {formatCrypto(transaction.fee)} {transaction.assets?.currency_code}
                                                    </td>
                                                    <td>
                                                        {getStatusBadge(transaction.status)}
                                                    </td>
                                                    <td className="admin-date-cell">
                                                        {new Date(transaction.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td>
                                                        <Button color="outline-primary" className="admin-view-btn">
                                                            <Eye size={14} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </TabPane>

                            {/* Assets Tab */}
                            <TabPane tabId="4" className="admin-tab-pane">
                                <div className="admin-section-header">
                                    <CardTitle tag="h5" className="admin-section-title">Assets Management</CardTitle>
                                    <Button color="primary" className="admin-btn admin-btn--primary">
                                        <Plus size={16} className="admin-btn-icon" />
                                        Add Asset
                                    </Button>
                                </div>
                                <Row className="admin-assets-grid">
                                    {assets.map(asset => (
                                        <Col key={asset.id} md={6} lg={4} className="admin-asset-col">
                                            <Card className="admin-asset-card">
                                                <CardBody>
                                                    <div className="admin-asset-header">
                                                        <div className="admin-asset-info">
                                                            <div className="admin-asset-icon-wrapper">
                                                                <div
                                                                    className="admin-asset-icon"
                                                                    style={{
                                                                        backgroundColor: asset.color_hex || '#6c757d'
                                                                    }}
                                                                >
                                                                    {asset.symbol || asset.currency_code.charAt(0)}
                                                                </div>
                                                            </div>
                                                            <div className="admin-asset-details">
                                                                <CardTitle tag="h6" className="admin-asset-name">
                                                                    {asset.currency_name}
                                                                </CardTitle>
                                                                <CardText className="admin-asset-code">
                                                                    {asset.currency_code}
                                                                </CardText>
                                                            </div>
                                                        </div>
                                                        <div className="admin-asset-status">
                                                            <Badge color={asset.asset_type === 'fiat' ? 'success' : 'warning'} className="admin-asset-type-badge">
                                                                {asset.asset_type}
                                                            </Badge>
                                                            <Badge color={asset.is_active ? 'success' : 'danger'} className="admin-asset-active-badge">
                                                                {asset.is_active ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    <div className="admin-asset-specs">
                                                        <div className="admin-asset-spec">
                                                            <span>Min Deposit:</span>
                                                            <span>{asset.min_deposit_amount}</span>
                                                        </div>
                                                        <div className="admin-asset-spec">
                                                            <span>Deposit Fee:</span>
                                                            <span>{asset.deposit_fee_percent}%</span>
                                                        </div>
                                                        <div className="admin-asset-spec">
                                                            <span>Withdrawal Fee:</span>
                                                            <span>{asset.withdrawal_fee_percent}%</span>
                                                        </div>
                                                    </div>

                                                    <div className="admin-asset-actions">
                                                        <Button color="outline-primary" className="admin-btn admin-btn--outline admin-btn--block">
                                                            <Edit size={14} className="admin-btn-icon" />
                                                            Edit
                                                        </Button>
                                                        <Button color="outline-danger" className="admin-btn admin-btn--outline admin-btn--block">
                                                            <Trash2 size={14} className="admin-btn-icon" />
                                                            Delete
                                                        </Button>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </TabPane>

                            {/* Activity Log Tab */}
                            <TabPane tabId="5" className="admin-tab-pane">
                                <div className="admin-section-header">
                                    <CardTitle tag="h5" className="admin-section-title">System Activity Log</CardTitle>
                                    <Button color="outline-secondary" className="admin-btn admin-btn--outline">
                                        <Download size={16} className="admin-btn-icon" />
                                        Export Log
                                    </Button>
                                </div>
                                <div className="admin-table-responsive">
                                    <Table hover className="admin-table admin-table--activity">
                                        <thead className="admin-table-head">
                                            <tr>
                                                <th>Time</th>
                                                <th>User</th>
                                                <th>Activity Type</th>
                                                <th>Description</th>
                                                <th>IP Address</th>
                                            </tr>
                                        </thead>
                                        <tbody className="admin-table-body">
                                            {userActivities.map(activity => (
                                                <tr key={activity.id} className="admin-table-row">
                                                    <td className="admin-date-cell">
                                                        <small className="admin-activity-time">
                                                            {new Date(activity.created_at).toLocaleString()}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        {activity.profiles ?
                                                            `${activity.profiles.first_name} ${activity.profiles.last_name}` :
                                                            'System'
                                                        }
                                                    </td>
                                                    <td>
                                                        <Badge color="info" className="admin-activity-type-badge">
                                                            {activity.activity_type.replace('_', ' ')}
                                                        </Badge>
                                                    </td>
                                                    <td className="admin-activity-description">{activity.description}</td>
                                                    <td className="admin-ip-cell">
                                                        <small className="admin-ip-address">
                                                            {activity.ip_address || 'N/A'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </TabPane>
                        </TabContent>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default AdminDashboard;